import { TOKEN_PROGRAM_ID } from '@solana/spl-token';
import { TransactionResponse, VersionedTransactionResponse } from '@solana/web3.js';

type InstructionError = [index: number, errorMsg: string];
type CustomInstructionError = [index: number, code: { Custom: number }];
interface ITransactionError {
  InstructionError: CustomInstructionError | InstructionError;
}

interface IInsufficientFundsForRent {
  InsufficientFundsForRent: {
    account_index: number;
  };
}

export class TransactionError extends Error {
  constructor(
    m: string,
    public txid?: string,
    public code?: number,
    public programIds?: string[],
    public serializedTx?: string,
  ) {
    super(m);

    // Set the prototype explicitly.
    Object.setPrototypeOf(this, Error.prototype);
  }
}
export const COMMON_TX_ERRORS = {
  PROGRAM_FAILED_TO_COMPLETE_ERROR: 'Program failed to complete',
  UNKNOWN_ERROR: 'Unknown error, visit the explorer',
  TOO_MANY_ACCOUNT_LOCKS: 'Transaction locked too many accounts',
  INSUFFICIENT_FUNDS_FOR_RENT: 'The account does not have enough SOL to perform the operation',
  TRANSACTION_TOO_LARGE: 'Transaction too large',
  TRANSACTION_NOT_CONFIRMED: 'Transaction has expired and was not confirmed. Please try again.',
  FROZEN_ACCOUNT: 'The token account is frozen',
  TRANSACTION_EXPIRED_BLOCKHEIGHT_EXCEEDED: 'TransactionExpiredBlockheightExceededError',
};

const SYSTEM_PROGRAM_ID = '11111111111111111111111111111111';

export type ParseErrorParam = Pick<
  NonNullable<Pick<VersionedTransactionResponse | TransactionResponse, 'meta'>['meta']>,
  'err' | 'logMessages'
>;
export function parseErrorForTransaction({ err, logMessages }: ParseErrorParam): {
  message: string;
  programIds?: string[];
  code?: number;
} {
  const transactionError = err;
  let errorCode;
  if (transactionError && typeof transactionError !== 'string') {
    let error = transactionError as ITransactionError | IInsufficientFundsForRent;

    if ('InsufficientFundsForRent' in error) {
      return {
        message: COMMON_TX_ERRORS.INSUFFICIENT_FUNDS_FOR_RENT,
        code: 1,
        programIds: [TOKEN_PROGRAM_ID.toBase58()],
      };
    } else {
      const instructionError = (transactionError as ITransactionError).InstructionError;

      const [_, errorMsg] = instructionError;

      if (typeof errorMsg === 'string') {
        if (errorMsg === 'ProgramFailedToComplete') {
          if (logMessages) {
            const failedProgramIds = getFailedExceededInstructionsPrograms(logMessages);
            if (failedProgramIds.length > 0) {
              return {
                message: COMMON_TX_ERRORS.PROGRAM_FAILED_TO_COMPLETE_ERROR,
                programIds: failedProgramIds,
                code: errorCode,
              };
            }
          }
        }
      } else {
        errorCode = errorMsg.Custom ?? (instructionError[1] as any as number);
        if (logMessages) {
          const failedProgramId = getFailedProgram(logMessages, errorCode);
          if (failedProgramId) {
            if (failedProgramId === SYSTEM_PROGRAM_ID) {
              return getSystemProgramError(instructionError as CustomInstructionError);
            }

            if (failedProgramId === TOKEN_PROGRAM_ID.toBase58()) {
              switch (errorCode) {
                case 17: {
                  return {
                    message: COMMON_TX_ERRORS.FROZEN_ACCOUNT,
                    programIds: [failedProgramId],
                    code: errorCode,
                  };
                }
              }
            }

            return {
              message: COMMON_TX_ERRORS.UNKNOWN_ERROR,
              programIds: [failedProgramId],
              code: errorCode,
            };
          }
        }
      }
    }
  }

  if (transactionError === 'InsufficientFundsForFee') {
    return {
      message: COMMON_TX_ERRORS.INSUFFICIENT_FUNDS_FOR_RENT,
      code: 1,
      programIds: [TOKEN_PROGRAM_ID.toBase58()],
    };
  } else if (transactionError === 'TooManyAccountLocks') {
    return {
      message: COMMON_TX_ERRORS.TOO_MANY_ACCOUNT_LOCKS,
      code: 0,
      programIds: [],
    };
  }

  return {
    message:
      typeof transactionError === 'string'
        ? transactionError || COMMON_TX_ERRORS.UNKNOWN_ERROR
        : COMMON_TX_ERRORS.UNKNOWN_ERROR,
    code: errorCode,
  };
}

function getFailedExceededInstructionsPrograms(logMessages: String[]) {
  let founds = [];
  for (let i = 0; i < logMessages.length; i++) {
    const log = logMessages[i];

    const found = log.match(new RegExp(/Program (\S+) failed: (?:Program failed to complete|SBF program panicked)/));

    if (found) {
      founds.push(found[1]);
    }
  }

  return founds;
}

function getFailedProgram(logMessages: String[], errorCode: number | string) {
  for (let i = 0; i < logMessages.length; i++) {
    const log = logMessages[i];

    const found = log.match(
      new RegExp(`Program ([1-9A-HJ-NP-Za-km-z]{32,44}) failed: custom program error: 0x${errorCode.toString(16)}`),
    );

    if (found) {
      return found[1];
    }
  }

  return;
}

function getSystemProgramError(instructionError: CustomInstructionError) {
  const code = instructionError[1].Custom;

  let message = '';
  switch (code) {
    // https://github.com/solana-labs/solana/blob/22a18a68e3ee68ae013d647e62e12128433d7230/sdk/program/src/system_instruction.rs#L12-L26
    // TODO: Do we need to translate all error codes.
    case 0: {
      message = 'An account with the same address already exists';
      break;
    }
    case 1: {
      message = COMMON_TX_ERRORS.INSUFFICIENT_FUNDS_FOR_RENT;
      break;
    }
    default: {
      message = COMMON_TX_ERRORS.UNKNOWN_ERROR;
    }
  }

  return {
    code,
    programIds: [SYSTEM_PROGRAM_ID],
    message,
  };
}
